import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import {
  ApplicationResponse,
  DataModelEnum,
  Form,
  Permission,
  PermissionAction,
  Student,
  StudentApplicationFilterResource,
  StudentApplicationResponseDto,
} from "generated/openApi";
import { PublicApplicationQuestionPage } from "pages/public-forms/applications/PublicApplicationQuestionPage";
import { useContext, useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

type StudentApplicationHeader = {
  rootPermission: Permission;
  crudMode: CrudMode;
  applicationDto: StudentApplicationResponseDto | undefined;
  updateApplicationDto: (dto: StudentApplicationResponseDto) => void;
  isApproving: boolean;
  hasChanges: boolean;
  updateIsApproving: (value: boolean) => void;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  newStudent: Student;
  exportApplication: () => void;
};

export const StudentApplicationHeader = ({
  rootPermission,
  crudMode,
  applicationDto,
  isApproving,
  hasChanges,
  updateIsApproving,
  updateApplicationDto,
  setValidationErrors,
  unsavedChanges,
  newStudent,
  exportApplication,
}: StudentApplicationHeader) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resource, setResource] = useState<StudentApplicationFilterResource>({});
  const [menu, setMenu] = useState<MenuItemType[]>([]);
  const listContext = useContext<ListContextValue>(ListContext);
  

  useEffect(() => {
    ApiService.studentApplications.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  useEffect(() => {
    if (applicationDto) {
      let menuItems: MenuItemType[] = [];
      resource.applicationStatuses?.map((s) => {
        if (s.id == 2 && applicationDto?.applicationStatus?.id != 2) {
          menuItems.push({
            label: s.name,
            action: () => approveApplicationStart(),
          });
        }
        if (s.id == 4 && applicationDto?.applicationStatus?.id != 2 && applicationDto?.applicationStatus?.id != 4) {
          menuItems.push({
            label: s.name,
            action: () => waitListApplication(),
          });
        }
        if (s.id == 1 && applicationDto?.applicationStatus?.id != 2 && applicationDto?.applicationStatus?.id != 1) {
          menuItems.push({
            label: s.name,
            action: () => pendingApplication(),
          });
        }
        if (s.id == 3 && applicationDto?.applicationStatus?.id != 2 && applicationDto?.applicationStatus?.id != 3) {
          menuItems.push({
            label: <Typography color="error">{s.name}</Typography>,
            action: () => rejectApplication(),
          });
        }
        if (s.id > 4) {
          menuItems.push({
            label: s.name,
            action: () => updateApplicationStatus(s.id),
          });
        }
      })
      setMenu(menuItems);
    }
  }, [applicationDto]);

  const saveUpdateApplicationResponse = () => {
    ApiService.studentApplications
      .updateApplicationStatus(applicationDto!)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  }

  const save = () => {
    setValidationErrors({});
    ApiService.students
      .saveCreate(newStudent!)
      .then((studentId) => {
        approveApplication(studentId);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  const updateApplicationStatus = (applicationStatusId: number) => {
    updateIsApproving(false);
    var temp = { ...applicationDto, applicationStatusId: applicationStatusId };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const rejectApplication = () => {
    updateIsApproving(false);
    var temp = { ...applicationDto, applicationStatusId: 3 };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const approveApplicationStart = () => {
    updateIsApproving(true);
    var temp = applicationDto!;
    temp.applicationStatus!.name = "Approving...";
    updateApplicationDto(temp);
  };

  const approveApplication = (studentId : number) => {
    var temp = { ...applicationDto, applicationStatusId: 2, studentId: studentId };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            navigate("../");
            window.location.reload();
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const waitListApplication = () => {
    updateIsApproving(false);
    var temp: StudentApplicationResponseDto = { ...applicationDto, applicationStatusId: 4 };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const pendingApplication = () => {
    updateIsApproving(false);
    var temp: StudentApplicationResponseDto = { ...applicationDto, applicationStatusId: 1 };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  return (
    <>
      {applicationDto && (
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing="sm">
            <Typography variant="inherit">
            {applicationDto.lastName}, {applicationDto.firstName}
            </Typography>
            <Typography fontWeight="light">{applicationDto.applicationStatus?.name}</Typography>
            {menu.length > 0 && <DropDownMenu menuItems={menu!} />}
          </Stack>

          <Stack direction="row" alignItems="center">
            <CloseButton />
            {/* {(crudMode === "view" && !isApproving) && isAuthorized(rootPermission, PermissionAction.Update) && (
              <UpdateButton onClick={() => {}} />
            )} */}
            {/* <SubmitButton
              onClick={() => exportApplication()}
              label="Export"
            /> */}
            {isApproving && <SubmitButton onClick={() => save()} disabled={!unsavedChanges} />}
            {hasChanges && !isApproving && <SubmitButton onClick={() => saveUpdateApplicationResponse()} disabled={!unsavedChanges} />}
          </Stack>
        </Stack>
      )}
    </>
  );
};
